<script setup lang="ts">
import type { Feedback } from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { currentScreenLabel, feedbackScreens } from '../feedback';
import { useAssistantStore } from '../stores/assistant';
import { useAuthStore } from '../stores/auth';
import { useFeedbackPanelStore } from '../stores/feedbackPanel';
import { usePlanStore } from '../stores/plan';
import FeedbackForm from './FeedbackForm.vue';
import '../styles/assistant.css';
import '../styles/feedback.css';

/**
 * Lot AQ : panneau « Faire un retour » à droite, au style du tiroir de l'assistant (maquette retours-1410, onglet A,
 * repères 1-3, 6). Pas de voile : la page reste visible et utilisable à côté. « Où ? » suit l'écran affiché tant
 * qu'on ne l'a pas changé. Plein écran sur téléphone (largeur du tiroir plafonnée à 100 %).
 */
const store = useFeedbackPanelStore();
const auth = useAuthStore();
const assistant = useAssistantStore();
const planStore = usePlanStore();
const route = useRoute();
const form = ref<InstanceType<typeof FeedbackForm> | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
let opener: HTMLElement | null = null;

const screens = computed(() => feedbackScreens(auth.me?.role));
const routeScreen = computed(() => currentScreenLabel(route.meta.tab ?? route.name, screens.value));

function followRoute(): void {
  if (!store.screenChosen) store.draft.screen = routeScreen.value;
  else if (!screens.value.includes(store.draft.screen))
    store.draft.screen = currentScreenLabel(null, screens.value);
}

watch(
  () => store.open,
  async (open) => {
    if (open) {
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      followRoute();
      await nextTick();
      form.value?.focus();
    } else {
      // Retour au bouton qui a ouvert le panneau, sauf si l'assistant prend la place (il garde son focus).
      if (opener?.isConnected && !assistant.open) opener.focus();
      opener = null;
    }
  },
  { immediate: true },
);
// Le panneau reste ouvert pendant qu'on change d'écran : « Où ? » suit, sauf choix à la main.
watch(routeScreen, () => {
  if (store.open) followRoute();
});

function onScreen(value: string): void {
  store.draft.screen = value;
  store.screenChosen = true;
}

function onSent(feedback: Feedback, _complete: boolean, queued = false): void {
  store.markSent(feedback, queued);
  void nextTick(() => closeButton.value?.focus());
}

function anotherOne(): void {
  store.sent = null;
  followRoute();
  void nextTick(() => form.value?.focus());
}

function onKeydown(event: KeyboardEvent): void {
  // Échap ferme d'abord l'écran de validation d'un plan s'il est ouvert, pas le panneau.
  if (event.key === 'Escape' && store.open && !planStore.isOpen) store.hide();
}
onMounted(() => document.addEventListener('keydown', onKeydown, true));
onBeforeUnmount(() => document.removeEventListener('keydown', onKeydown, true));
</script>

<template>
  <aside
    v-if="store.open"
    class="drawer feedback-drawer"
    aria-label="Faire un retour"
    data-testid="feedback-panel"
  >
    <div class="card-h">
      <div>
        <h2>Faire un retour</h2>
        <p>Un bug, un chiffre bizarre, une idée. Maxime les reprend régulièrement.</p>
      </div>
      <button ref="closeButton" class="btn sm" type="button" @click="store.hide()">Fermer</button>
    </div>
    <div v-if="store.sent" class="feedback-sent" role="status" data-testid="feedback-panel-sent">
      <template v-if="store.queued">
        <p><b>Merci ! Ton retour est gardé sur le téléphone.</b></p>
        <p class="muted" data-testid="feedback-panel-queued">
          Il partira avec ses captures dès que le réseau reviendra.
        </p>
      </template>
      <p v-else><b>Merci ! Ton retour est enregistré.</b></p>
      <p v-if="!store.queued" class="muted">
        {{
          store.sent.attachments.length === 0
            ? 'Sans capture.'
            : store.sent.attachments.length === 1
              ? '1 capture jointe.'
              : `${store.sent.attachments.length} captures jointes.`
        }}
        Tu le retrouves avec les autres sur la page Retours, avec la réponse de Maxime quand il l’aura traité.
      </p>
      <div class="row-actions">
        <RouterLink
          class="btn sm primary"
          to="/retours"
          data-testid="feedback-panel-link"
          @click="store.hide()"
          >Voir la page Retours</RouterLink
        >
        <button class="btn sm" type="button" @click="anotherOne">Faire un autre retour</button>
      </div>
    </div>
    <FeedbackForm
      v-else
      ref="form"
      v-model:type="store.draft.type"
      v-model:text="store.draft.text"
      v-model:files="store.draft.files"
      v-model:conversation-id="store.draft.conversationId"
      v-model:observed="store.draft.observed"
      :screen="store.draft.screen"
      :screens="screens"
      id-prefix="fbp"
      :context-route="route.fullPath"
      @update:screen="onScreen"
      @sent="onSent"
    >
      <template #where-hint>
        <span v-if="!store.screenChosen" class="feedback-from">l’écran où tu es</span>
      </template>
      <template #actions="{ sending }">
        <button class="btn" type="button" :disabled="sending" @click="store.discard()">Annuler</button>
        <button class="btn primary" type="submit" :disabled="sending">Envoyer le retour</button>
      </template>
    </FeedbackForm>
  </aside>
</template>
